import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { IMAGES, CAPABILITIES, WHATSAPP_LINK } from "../../data/site";

export const Manufacturing = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

    return (
        <section id="manufacturing" ref={ref} data-testid="manufacturing-section" className="bg-white py-24 lg:py-32">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="lg:col-span-6"
                >
                    <p className="font-mono text-[11px] tracking-[0.35em] text-gold mb-6" data-testid="manufacturing-overline">
                        03 / CUSTOM MANUFACTURING & OEM
                    </p>
                    <h2 className="font-heading font-extrabold tracking-tighter text-navy text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="manufacturing-heading">
                        BUILT TO YOUR BLUEPRINT, NOT OUR CATALOG.
                    </h2>
                    <p className="mt-6 text-muted text-base md:text-lg leading-relaxed" data-testid="manufacturing-copy">
                        From a single executive cabin to a 1,000-seat IT floor, our Erragadda plant runs
                        custom dimensions, brand-matched finishes and OEM production — with space planning
                        support for architects and interior contractors.
                    </p>

                    <ul className="mt-10 space-y-4" data-testid="capabilities-list">
                        {CAPABILITIES.map((cap, i) => (
                            <motion.li
                                key={cap}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.08 }}
                                className="flex items-start gap-4 border-b border-line pb-4"
                            >
                                <span className="mt-0.5 w-6 h-6 shrink-0 bg-navy flex items-center justify-center">
                                    <Check size={13} className="text-gold" />
                                </span>
                                <span className="text-charcoal text-sm md:text-base">{cap}</span>
                            </motion.li>
                        ))}
                    </ul>

                    <a
                        href={WHATSAPP_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group mt-10 inline-flex items-center gap-2 px-8 py-4 bg-navy text-white font-medium tracking-wide hover:bg-gold transition-colors duration-300"
                        data-testid="factory-team-button"
                    >
                        Talk to Our Factory Team
                        <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </a>
                </motion.div>

                <div className="lg:col-span-6 relative">
                    <motion.div
                        initial={{ clipPath: "inset(0 0 100% 0)" }}
                        whileInView={{ clipPath: "inset(0 0 0% 0)" }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                        className="relative h-[420px] md:h-[560px] overflow-hidden"
                    >
                        <motion.img
                            src={IMAGES.factory}
                            alt="TASKLANCE furniture manufacturing craftsmanship"
                            loading="lazy"
                            style={{ y }}
                            className="absolute inset-0 w-full h-[120%] object-cover -top-[10%]"
                            data-testid="factory-image"
                        />
                        <div className="absolute inset-0 bg-navy/20" />
                    </motion.div>
                    <div className="absolute -bottom-6 -left-6 bg-navy px-8 py-6 hidden md:block" data-testid="factory-badge">
                        <p className="font-mono text-[10px] tracking-[0.3em] text-gold">ERRAGADDA PLANT</p>
                        <p className="font-heading font-bold text-white text-xl mt-1 tracking-tight">In-house. End to end.</p>
                    </div>
                </div>
            </div>
        </section>
    );
};
